import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { fmtIDR, apiErr, isInternal } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Trash2, RefreshCw, Inbox, Landmark, CalendarRange } from "lucide-react";
import { AssetForm } from "@/components/assets/AssetForm";
import { DepreciationTab } from "@/components/assets/DepreciationTab";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";

const STATUS = { active: ["Aktif", "default"], fully_depreciated: ["Habis Disusutkan", "secondary"], disposed: ["Dilepas", "outline"] };
const ageText = (m) => (m >= 12 ? `${Math.floor(m / 12)} th ${m % 12} bln` : `${m} bln`);

function Stat({ label, value, tid }) {
  return (
    <Card className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className="num text-lg font-semibold mt-1" data-testid={tid}>{fmtIDR(value)}</p></Card>
  );
}

function CandidatesTab({ clientId, canWrite, categories, onDone }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await api.get(`/fixed-assets/candidates?client_id=${clientId}`); setRows(data); } catch (e) { setRows([]); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onDone(); };
  return (
    <Card className="p-4 space-y-3">
      <p className="text-xs text-muted-foreground">Transaksi pengeluaran dengan akun Aset Tetap (kode 15xx–19xx) yang belum terdaftar di daftar aset.</p>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead>Tanggal</TableHead><TableHead>Vendor / Uraian</TableHead><TableHead>Akun</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">Tidak ada transaksi aset tetap yang belum didaftarkan</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`candidate-row-${r.id}`}>
                <TableCell className="num text-xs">{fmtDate(r.date)}</TableCell>
                <TableCell className="text-sm">{r.vendor || "—"}<div className="text-[11px] text-muted-foreground">{r.description}{r.invoice_no ? ` · ${r.invoice_no}` : ""}</div></TableCell>
                <TableCell className="text-xs">{r.coa_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.status}</Badge></TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(r.amount)}</TableCell>
                <TableCell className="text-right">{canWrite && <AssetForm clientId={clientId} categories={categories} fromTxn={r} onDone={done}
                  trigger={<Button size="sm" variant="outline" data-testid={`candidate-register-${r.id}`}>Daftarkan</Button>} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function AssetTable({ data, clientId, categories, canWrite, onDone }) {
  const tools = useTableTools(data.rows, { date: (a) => a.acquisition_date, text: (a) => `${a.code} ${a.name} ${a.category_label} ${(STATUS[a.status] || STATUS.active)[0]}` });
  const del = async (a) => {
    if (!window.confirm(`Hapus aset ${a.name}? Jurnal penyusutannya akan disesuaikan otomatis.`)) return;
    try { await api.delete(`/fixed-assets/${a.id}`); toast.success("Aset dihapus"); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDel = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/fixed-assets/${id}`)); onDone(); return r; };
  const cols = [["Kode", "code"], ["Nama Aset", "name"], ["Kelompok", "category_label"], ["Metode", (a) => (a.method === "declining" ? "Saldo Menurun" : "Garis Lurus")],
    ["Tarif %", "rate"], ["Perolehan", "acquisition_date"], ["Harga Perolehan", "cost"], ["Penyusutan/Bln", "monthly_depreciation"], ["Akumulasi", "accumulated"],
    ["Nilai Buku", "nbv"], ["Status", (a) => (STATUS[a.status] || STATUS.active)[0]]];
  return (
    <div className="space-y-3">
    <TableToolbar tools={tools} id="asset" cols={cols} filename="aset_tetap" onDelete={canWrite ? bulkDel : undefined} />
    <div className="overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader><TableRow className="bg-secondary/50">
          <TableHead className="w-8"><SelectAllBox tools={tools} id="asset" /></TableHead>
          <TableHead>Kode</TableHead><TableHead>Nama Aset</TableHead><TableHead>Kelompok</TableHead><TableHead>Metode</TableHead><TableHead>Perolehan</TableHead>
          <TableHead className="text-right">Harga Perolehan</TableHead><TableHead className="text-right">Penyusutan / Bln</TableHead><TableHead className="text-right">Akumulasi</TableHead>
          <TableHead className="text-right">Nilai Buku</TableHead><TableHead>Umur</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {tools.view.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground text-sm">Belum ada aset tetap. Tambah manual atau daftarkan dari tab "Dari Transaksi".</TableCell></TableRow>}
          {tools.view.map((a) => (
            <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
              <TableCell><SelectBox tools={tools} row={a} id="asset" /></TableCell>
              <TableCell className="text-xs font-mono">{a.code}</TableCell>
              <TableCell className="text-sm font-medium">{a.name}{a.kind === "intangible" && <span className="ml-1 text-[10px] text-violet-600">(amortisasi)</span>}</TableCell>
              <TableCell className="text-xs">{a.category_label}</TableCell>
              <TableCell className="text-xs">{a.method === "declining" ? "Saldo Menurun" : "Garis Lurus"} · {a.rate}%</TableCell>
              <TableCell className="num text-xs">{fmtDate(a.acquisition_date)}</TableCell>
              <TableCell className="num text-right text-xs">{fmtIDR(a.cost)}</TableCell>
              <TableCell className="num text-right text-xs" data-testid={`asset-monthly-${a.id}`}>{fmtIDR(a.monthly_depreciation)}</TableCell>
              <TableCell className="num text-right text-xs">{fmtIDR(a.accumulated)}</TableCell>
              <TableCell className="num text-right text-xs font-semibold" data-testid={`asset-nbv-${a.id}`}>{fmtIDR(a.nbv)}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{ageText(a.age_months)}{a.life_months ? <span className="block text-[10px] text-muted-foreground">sisa {ageText(a.remaining_months)}</span> : null}</TableCell>
              <TableCell><Badge variant={(STATUS[a.status] || STATUS.active)[1]} className="text-[10px]">{(STATUS[a.status] || STATUS.active)[0]}</Badge></TableCell>
              <TableCell className="text-right whitespace-nowrap">{canWrite && <>
                <AssetForm clientId={clientId} categories={categories} editAsset={a} onDone={onDone} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`asset-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>} />
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(a)} data-testid={`asset-del-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
              </>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
}

export default function FixedAssets() {
  const { user, selectedClientId, clients } = useAuth();
  const { t } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const canWrite = isInternal(user.role);
  const [data, setData] = useState({ rows: [] });
  const [categories, setCategories] = useState([]);
  const load = useCallback(async () => {
    if (!clientId) return;
    const { data: d } = await api.get(`/fixed-assets?client_id=${clientId}`);
    setData(d);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/fixed-assets/categories").then(({ data: c }) => setCategories(c)).catch(() => {}); }, []);
  const syncJ = async () => {
    try { const { data: r } = await api.post(`/fixed-assets/sync-journals?client_id=${clientId}`); toast.success(`Jurnal penyusutan disinkronkan (${r.updated} periode diperbarui)`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  return (
    <div className="space-y-4" data-testid="fixed-assets-page">
      <div className="page-sticky flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("nav.assets")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelompok umur aset sesuai PMK 72/2023 · penyusutan & amortisasi bulanan otomatis dijurnal ke Laporan {clientName && `· ${clientName}`}</p>
        </div>
        {canWrite && <div className="flex gap-2">
          <Button variant="outline" onClick={syncJ} data-testid="asset-sync-journals"><RefreshCw className="h-4 w-4 mr-2" />Sinkronkan Jurnal</Button>
          <AssetForm clientId={clientId} categories={categories} onDone={load} />
        </div>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total Harga Perolehan" value={data.total_cost} tid="asset-total-cost" />
        <Stat label="Penyusutan Bulan Ini" value={data.total_monthly} tid="asset-total-monthly" />
        <Stat label="Akumulasi Penyusutan" value={data.total_accumulated} tid="asset-total-acc" />
        <Stat label="Total Nilai Buku" value={data.total_nbv} tid="asset-total-nbv" />
      </div>
      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="asset-tab-list"><Landmark className="h-4 w-4 mr-1.5" />Daftar Aset</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="asset-tab-candidates"><Inbox className="h-4 w-4 mr-1.5" />Dari Transaksi</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="asset-tab-schedule"><CalendarRange className="h-4 w-4 mr-1.5" />Jadwal Penyusutan</TabsTrigger>
        </TabsList>
        <TabsContent value="list"><AssetTable data={data} clientId={clientId} categories={categories} canWrite={canWrite} onDone={load} /></TabsContent>
        <TabsContent value="candidates"><CandidatesTab clientId={clientId} canWrite={canWrite} categories={categories} onDone={load} /></TabsContent>
        <TabsContent value="schedule"><DepreciationTab clientId={clientId} /></TabsContent>
      </Tabs>
    </div>
  );
}
